import React from 'react';

import { formatMoney, randomUUID, toCentsFromString, toMoney } from '@betterment/core';
import { Box, Button, FormikField, Icon, Input, Table, Text, Tooltip } from '@betterment/samba-react';

import { cellBorderStyle, lastRowCellStyle, tableBorderStyles } from './styles';
import { TableRow } from './TableRow';

import { MAX_TIERS } from '../constants';
import { FormTier } from '../data';

interface TableProps {
  tiers: FormTier[];
  addTier: (tier: FormTier) => void;
  removeTier?: (index: number) => void;
}

export const TieredBillingTable: React.FC<TableProps> = ({ tiers, addTier, removeTier }) => {
  const tierStartForLastTier = (): string => {
    const previousTierEnd = tiers[tiers.length - 1].tierEnd;
    if (previousTierEnd) {
      return formatMoney(toMoney(toCentsFromString(previousTierEnd.replace(/,/g, ''))), 'defaultOptional');
    }
    // show a default of $0.00
    return formatMoney(toMoney(0));
  };
  return (
    <Table css={{ ...tableBorderStyles, borderRadius: 'medium' }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell css={{ ...cellBorderStyle, width: '80px', textAlign: 'center' }}>
            Tier
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell css={{ width: '388px', ...cellBorderStyle }}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              Invested assets
              <Tooltip>
                <Tooltip.Trigger asChild={true}>
                  <Tooltip.IconButton css={{ ml: 'space.50' }} />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <Tooltip.Text>
                    The asset range ends at the number inputted. The next tier starts at the number inputted plus $0.01.
                  </Tooltip.Text>
                </Tooltip.Content>
              </Tooltip>
            </Box>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell css={{ ...cellBorderStyle }}>Annual advisory fee</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {tiers.map((tier, index) => (
          // @ts-expect-error TS2322 violates strict null checks
          <TableRow key={index} index={index} removeTier={removeTier} /> // eslint-disable-line react/no-array-index-key
        ))}
        {/* Last tier is rendered separately since it does not require a tierEnd */}
        <Table.Row css={{ height: 'size.800' }} key={tiers.length}>
          <Table.Cell
            css={{
              ...(tiers.length < MAX_TIERS ? cellBorderStyle : lastRowCellStyle),
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            <Text>{tiers.length + 1}</Text>
          </Table.Cell>
          <Table.Cell
            css={{ ...(tiers.length < MAX_TIERS ? cellBorderStyle : lastRowCellStyle), verticalAlign: 'middle' }}
          >
            <Box css={{ display: 'grid', alignItems: 'center', gridTemplateColumns: '.75fr 0.25fr 1fr' }}>
              <Text>{tierStartForLastTier()}</Text>
              <Icon name="RightArrow" size="small" css={{ marginRight: 'space.50' }} />
              <Text>And up</Text>
            </Box>
          </Table.Cell>
          <Table.Cell
            css={{
              ...(tiers.length < MAX_TIERS ? cellBorderStyle : lastRowCellStyle),
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            <Box css={{ display: 'grid', alignItems: 'center', gridTemplateColumns: '1fr .75fr' }}>
              <FormikField name="lastTier.firmFee.feeValue">
                <FormikField.Control>
                  <Input spacing="compact" aria-label="Last tier fee rate" mask="number" trailingSymbol="bps" />
                </FormikField.Control>
                <FormikField.ErrorMessage />
              </FormikField>
            </Box>
          </Table.Cell>
        </Table.Row>
      </Table.Body>
      <Table.Footer>
        {tiers.length < MAX_TIERS && (
          <Table.Row>
            <Table.Cell colSpan={3} css={{ textAlign: 'left' }}>
              <Button
                css={{ display: 'flex' }}
                variant="textPrimary"
                onClick={() =>
                  addTier({
                    uuid: randomUUID(),
                    tierEnd: '',
                    firmFee: {
                      feeConfigType: 'assetBasedConfiguration',
                      feeValue: '',
                    },
                  })
                }
              >
                <Icon name="Plus" size="small" css={{ marginRight: 'space.50', verticalAlign: 'middle' }} />
                Add tier
              </Button>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Footer>
    </Table>
  );
};
